import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { LoginInput } from '@lub0v/santa-utils';
import {
  AuthFrame,
  AuthSocial,
  Button,
  Checkbox,
  Divider,
  HintError,
  Input,
  EmailInput,
  Link,
  Stack,
} from '@src/components';
import { useEnterOrder } from '@src/forms/useEnterOrder';
import {
  loginWithEmail,
  loginWithGoogle,
  loginWithTelegram,
  loginWithVk,
} from '@src/pages/auth/auth';
import { useValidatedForm } from '@src/validation';

const LOGIN_ENTER = ['login-email', 'login-password', 'login-consent', 'login-submit'] as const;

export function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const form = useValidatedForm(LoginInput, { email: '', password: '' });
  const enter = useEnterOrder(LOGIN_ENTER);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<string | null>) {
    setError(null);
    setBusy(true);
    try {
      const message = await action();
      if (message) {
        setError(message);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'login.failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthFrame
      title={t('login.title')}
      subtitle={
        <Trans
          i18nKey="login.subtitle"
          components={{ register: <Link href="/register" variant="inline" /> }}
        />
      }
      testId="login"
    >
      <Stack gap={6}>
        <AuthSocial
          testId="login"
          disabled={busy || !consent}
          disabledHint={!consent ? t('login.consentRequired') : undefined}
          labels={{
            google: t('login.google'),
            telegram: t('login.telegram'),
            vk: t('login.vk'),
          }}
          onGoogle={() => run(() => loginWithGoogle(navigate))}
          onTelegram={() => run(() => loginWithTelegram(navigate))}
          onVk={() => run(() => loginWithVk(navigate))}
        />
        <Divider>{t('login.or')}</Divider>
        <EmailInput
          label={t('login.email')}
          autoComplete="email"
          placeholder="anna@example.com"
          testId="login-email"
          {...form.field('email')}
          {...enter.bind('login-email')}
        />
        <Input
          label={t('login.password')}
          password
          autoComplete="current-password"
          revealLabel={t('login.reveal')}
          hideLabel={t('login.hide')}
          labelEnd={
            <Link href="/recover" variant="quiet">
              {t('login.forgot')}
            </Link>
          }
          testId="login-password"
          {...form.field('password')}
          {...enter.bind('login-password')}
        />
        <Checkbox
          checked={consent}
          onCheckedChange={setConsent}
          testId="login-consent"
          {...enter.bind('login-consent')}
          label={
            <Trans
              i18nKey="login.consent"
              components={{
                terms: <Link href="/terms" variant="inline" />,
                privacy: <Link href="/policy" variant="inline" />,
              }}
            />
          }
        />
        <HintError error={error} testId="login-error" />
        <Button
          type="button"
          variant="primary"
          size="large"
          fullWidth
          disabled={busy || !consent || !form.filled}
          testId="login-submit"
          onClick={() =>
            void form.submit((values) => run(() => loginWithEmail(navigate, values.email, values.password)))
          }
        >
          {t('login.submit')}
        </Button>
        <Stack align="center" wide>
          <Stack align="stretch" wide>
            <Link href="/recover" variant="quiet" testId="login-cant">
              {t('login.cant')}
            </Link>
          </Stack>
        </Stack>
      </Stack>
    </AuthFrame>
  );
}
